import { useState } from "react";
import { motion } from "framer-motion";
import { Truck, Users, MapPin, Calendar, CheckCircle2, AlertCircle, Trash2, UserMinus, UserPlus, Pencil, Clock3, Archive, ArchiveRestore, ClipboardCheck, Stethoscope, Repeat, Copy } from "lucide-react";
import { ChecklistDialog } from "@/components/ChecklistDialog";
import { EndServiceButton } from "@/components/EndServiceButton";
import { WhatsAppShareButton, WhatsAppGroupLink } from "@/components/WhatsAppShare";
import { ChangeVehicleDialog } from "@/components/ChangeVehicleDialog";
import { GoogleCalendarButton } from "@/components/GoogleCalendarButton";
import { ServiceRoster } from "@/components/ServiceRoster";
import { AppealButton } from "@/components/AppealButton";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useSettings } from "@/context/SettingsContext";
import { typeColor, textOn } from "@/lib/typeColors";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function ServiceCard({ svc, currentUser, onSignup, onWithdraw, onDelete, onEdit, onDuplicate, onConsuntivo, onIscritti, onArchive, onRefresh, selectMode, selected, onToggleSelect }) {
  const covered = svc.status === "coperto";
  const canManage = currentUser.role === "coordinatore";
  const [changingVeh, setChangingVeh] = useState(false);
  const hasVehicles = (svc.ambulances?.length || 0) + (svc.required_vehicles?.length || 0) > 0;
  const history = svc.archived;
  const closed = history || svc.is_past;
  const volPct = svc.required_volunteers ? (svc.volunteers_count / svc.required_volunteers) * 100 : 100;
  const ambPct = svc.required_ambulances ? (svc.ambulances_count / svc.required_ambulances) * 100 : 100;
  const isAutista = (currentUser.qualifications || []).includes("Autista");
  const sanRoles = [
    { slot: "medico", qual: "Medico", label: "Medici", btn: "Medico", req: svc.required_medici || 0, count: svc.medici_count || 0 },
    { slot: "infermiere", qual: "Infermiere", label: "Infermieri", btn: "Infermiere", req: svc.required_infermieri || 0, count: svc.infermieri_count || 0 },
  ].filter((r) => r.req > 0);
  const { settings } = useSettings();
  const tColor = typeColor(svc.service_type, settings);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const showChecklist = covered && (svc.is_signed_up || canManage);
  const ambDisabled = !isAutista || svc.required_ambulances === 0 || svc.ambulances_count >= svc.required_ambulances;
  const ambTitle = !isAutista
    ? "Serve la qualifica Autista per questo ruolo"
    : svc.required_ambulances === 0
    ? "Questo servizio non richiede ambulanze"
    : svc.ambulances_count >= svc.required_ambulances
    ? "Posti ambulanza già coperti"
    : "Iscriviti come autista";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      data-testid={`service-card-${svc.id}`}
      style={{ borderTopColor: tColor }}
      className={`relative rounded-xl border border-border bg-card border-t-4 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col ${svc.archived ? "opacity-75" : ""}`}
    >
      {selectMode && (
        <div className="absolute top-3 right-3 z-10">
          <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(svc.id)} data-testid={`select-${svc.id}`} />
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-xl font-semibold tracking-tight truncate">{svc.event_name}</h3>
          <Badge style={{ backgroundColor: tColor, color: textOn(tColor), borderColor: tColor }} className="mt-2 text-xs font-bold uppercase tracking-wider border hover:opacity-90">
            {svc.service_type}
          </Badge>
        </div>
        {covered ? (
          <span data-testid={`status-badge-${svc.id}`} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-bold">
            <CheckCircle2 className="h-3.5 w-3.5" /> Coperto
          </span>
        ) : (
          <span data-testid={`status-badge-${svc.id}`} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 text-xs font-bold">
            <AlertCircle className="h-3.5 w-3.5" /> Aperto
          </span>
        )}
      </div>

      <div className="mt-4 space-y-2 text-sm text-muted-foreground">
        {(() => {
          const dateList = svc.dates && svc.dates.length ? svc.dates : [svc.date_time];
          const showCov = dateList.length > 1 && svc.required_volunteers > 0;
          return dateList.map((d, i) => {
            const covCount = (svc.signups || []).filter((s) => (s.slot === "volontario" || s.slot === "ambulanza") && (s.dates || []).includes(d)).length;
            const full = covCount >= svc.required_volunteers;
            return (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <Calendar className="h-4 w-4 text-[#D32F2F]" /> {fmtDate(d)}{svc.end_times?.[d] ? ` – ${svc.end_times[d]}` : ""}
                {showCov && (
                  <span data-testid={`date-coverage-${svc.id}-${i}`} className={`ml-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full border ${full ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {covCount}/{svc.required_volunteers} vol.
                  </span>
                )}
              </div>
            );
          });
        })()}
        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#D32F2F]" /> {svc.location}</div>
      </div>

      {svc.required_qualifications?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {svc.required_qualifications.map((q) => (
            <Badge key={q} variant="secondary" className="text-[11px]">{q}</Badge>
          ))}
        </div>
      )}

      <WhatsAppGroupLink svc={svc} />

      {svc.notes && (
        <p data-testid={`service-notes-${svc.id}`} className="mt-3 text-sm text-foreground/80 max-h-20 overflow-y-auto whitespace-pre-wrap pr-1">
          {svc.notes}
        </p>
      )}

      <div className="mt-4 space-y-3">
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="flex items-center gap-1.5 font-medium"><Users className="h-4 w-4" /> Volontari</span>
            <span className="font-mono font-semibold" data-testid={`vol-count-${svc.id}`}>{svc.volunteers_count}/{svc.required_volunteers}</span>
          </div>
          <Progress value={Math.min(volPct, 100)} className="h-2" />
        </div>
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="flex items-center gap-1.5 font-medium"><Truck className="h-4 w-4" /> Ambulanze</span>
            <span className="font-mono font-semibold" data-testid={`amb-count-${svc.id}`}>{svc.ambulances_count}/{svc.required_ambulances}</span>
          </div>
          <Progress value={Math.min(ambPct, 100)} className="h-2" />
          {svc.ambulances?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`amb-plates-${svc.id}`}>
              {svc.ambulances.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1 rounded-md border border-[#D32F2F]/30 bg-[#D32F2F]/5 px-2 py-0.5 text-xs">
                  {a.name}{a.plate && <b className="font-mono">{a.plate}</b>}
                </span>
              ))}
            </div>
          )}
        </div>
        {sanRoles.map((r) => (
          <div key={r.slot}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-1.5 font-medium"><Stethoscope className="h-4 w-4 text-[#D32F2F]" /> {r.label}</span>
              <span className="font-mono font-semibold" data-testid={`${r.slot}-count-${svc.id}`}>{r.count}/{r.req}</span>
            </div>
            <Progress value={Math.min((r.count / r.req) * 100, 100)} className="h-2" />
          </div>
        ))}
      </div>

      <ServiceRoster svc={svc} />

      {svc.required_vehicles?.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium flex items-center gap-1.5 mb-2"><Truck className="h-4 w-4" /> Altri mezzi richiesti</p>
          <div className="space-y-2">
            {svc.required_vehicles.map((v) => {
              const mine = v.driver_id === currentUser.id;
              const free = !v.driver_id;
              const blocked = v.requires_autista && !isAutista;
              return (
                <div key={v.id} data-testid={`vehicle-${svc.id}-${v.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{v.name}{v.type ? <span className="text-muted-foreground font-normal"> · {v.type}</span> : null}</p>
                    {v.driver_name ? (
                      <p className="text-[11px] text-emerald-700 font-medium">Autista: {mine ? "Tu" : v.driver_name}</p>
                    ) : (
                      <p className="text-[11px] text-amber-700 font-medium">{v.requires_autista ? "Serve un autista" : "Serve un conducente"}</p>
                    )}
                  </div>
                  {!closed && (
                    free ? (
                      <Button size="sm" variant="secondary" data-testid={`drive-${svc.id}-${v.id}`} disabled={blocked} title={blocked ? "Serve la qualifica Autista" : "Guida questo mezzo"} onClick={() => onSignup(svc, "mezzo", v.id)} className="rounded-full shrink-0">
                        <Truck className="h-4 w-4 mr-1" /> Guida
                      </Button>
                    ) : mine ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-200 shrink-0">Tu</Badge>
                    ) : (
                      <Badge variant="secondary" className="shrink-0">Assegnato</Badge>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {svc.is_past && !history && (
        <p className="mt-3 text-xs font-medium text-amber-700" data-testid={`awaiting-end-${svc.id}`}>Date passate: in attesa di «Fine servizio» per lo storico</p>
      )}

      {history && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={svc.status === "coperto" ? "text-emerald-700 border-emerald-200" : "text-amber-700 border-amber-200"}>
            Esito: {svc.status === "coperto" ? "Coperto" : "Scoperto"}
          </Badge>
          <Badge variant="secondary">Ore totali: {svc.total_hours}</Badge>
          {svc.archived && <Badge variant="outline" className="text-slate-600">Archiviato</Badge>}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">Coord. {svc.coordinator_name}</span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <EndServiceButton svc={svc} currentUser={currentUser} onEnded={onRefresh} />
          {!history && <GoogleCalendarButton svc={svc} />}
          {showChecklist && (
            <Button size="sm" variant="outline" title="Check-list vano sanitario" data-testid={`checklist-button-${svc.id}`} onClick={() => setChecklistOpen(true)} className="rounded-full">
              <ClipboardCheck className="h-4 w-4 mr-1" /> Check-list
            </Button>
          )}
          {canManage && (
            <>
              <WhatsAppShareButton svc={svc} />
              {!covered && !closed && <AppealButton svc={svc} onSent={onRefresh} />}
              {hasVehicles && (
                <Button size="sm" variant="ghost" title="Cambia mezzo" data-testid={`change-vehicle-${svc.id}`} onClick={() => setChangingVeh(true)} className="hover:bg-muted">
                  <Repeat className="h-4 w-4" />
                </Button>
              )}
              <ChangeVehicleDialog svc={svc} open={changingVeh} onOpenChange={setChangingVeh} onSaved={() => onRefresh && onRefresh()} />
              <Button size="sm" variant="ghost" title="Iscritti / Conferma" data-testid={`iscritti-service-${svc.id}`} onClick={() => onIscritti(svc)} className="hover:bg-muted">
                <Users className="h-4 w-4" />
              </Button>
              {onDuplicate && (
                <Button size="sm" variant="ghost" title="Duplica su una nuova data" data-testid={`duplicate-service-${svc.id}`} onClick={() => onDuplicate(svc)} className="hover:bg-muted">
                  <Copy className="h-4 w-4" />
                </Button>
              )}
              <Button size="sm" variant="ghost" title="Modifica servizio" data-testid={`edit-service-${svc.id}`} onClick={() => onEdit(svc)} className="hover:bg-muted">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" title="Consuntivo ore" data-testid={`consuntivo-service-${svc.id}`} onClick={() => onConsuntivo(svc)} className="hover:bg-muted">
                <Clock3 className="h-4 w-4" />
              </Button>
              {history ? (
                <Button size="sm" variant="ghost" title="Ripristina" data-testid={`restore-service-${svc.id}`} onClick={() => onArchive(svc, false)} className="hover:bg-muted">
                  <ArchiveRestore className="h-4 w-4" />
                </Button>
              ) : (
                <Button size="sm" variant="ghost" title="Archivia servizio" data-testid={`archive-service-${svc.id}`} onClick={() => onArchive(svc, true)} className="hover:bg-muted">
                  <Archive className="h-4 w-4" />
                </Button>
              )}
              <Button size="sm" variant="ghost" title="Elimina servizio" data-testid={`delete-service-${svc.id}`} onClick={() => onDelete(svc)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
          {!closed && (svc.is_signed_up ? (
            <div className="flex items-center gap-2">
              {svc.my_confirmed ? (
                <span data-testid={`confirmed-badge-${svc.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-xs font-bold">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Confermato
                </span>
              ) : (
                <span data-testid={`pending-badge-${svc.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 text-xs font-bold">
                  In attesa
                </span>
              )}
              <Button size="sm" variant="outline" data-testid={`withdraw-button-${svc.id}`} onClick={() => onWithdraw(svc)} className="rounded-full">
                <UserMinus className="h-4 w-4 mr-1" /> Ritirati
              </Button>
              {svc.dates && svc.dates.length > 1 && (svc.my_dates?.length || 0) < svc.dates.length && (
                <Button size="sm" data-testid={`add-dates-button-${svc.id}`} onClick={() => onSignup(svc, svc.my_slot || "volontario")} className="rounded-full">
                  <UserPlus className="h-4 w-4 mr-1" /> Aggiungi date
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              <Button size="sm" data-testid={`signup-vol-button-${svc.id}`} onClick={() => onSignup(svc, "volontario")} className="rounded-full">
                <UserPlus className="h-4 w-4 mr-1" /> Volontario
              </Button>
              <Button size="sm" variant="secondary" title={ambTitle} data-testid={`signup-amb-button-${svc.id}`} onClick={() => onSignup(svc, "ambulanza")} disabled={ambDisabled} className="rounded-full">
                <Truck className="h-4 w-4 mr-1" /> Ambulanza
              </Button>
              {sanRoles.filter((r) => (currentUser.qualifications || []).includes(r.qual)).map((r) => (
                <Button key={r.slot} size="sm" variant="outline" data-testid={`signup-${r.slot}-button-${svc.id}`} disabled={r.count >= r.req} title={r.count >= r.req ? "Posti già coperti" : `Iscriviti come ${r.btn.toLowerCase()}`} onClick={() => onSignup(svc, r.slot)} className="rounded-full border-[#D32F2F] text-[#D32F2F]">
                  <Stethoscope className="h-4 w-4 mr-1" /> {r.btn}
                </Button>
              ))}
            </div>
          ))}
        </div>
      </div>
      {showChecklist && <ChecklistDialog svc={svc} currentUser={currentUser} open={checklistOpen} onOpenChange={setChecklistOpen} />}
    </motion.div>
  );
}
